import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL_NEW } from '@utils/api/endpoints';
import { BaseError, BaseResponse } from '@utils/types/shared/base';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';

export type PricePlanType =
  | 'STT'
  | 'ALIGNMENT'
  | 'TRANSLATION'
  | 'REALTIME'
  | 'CUSTOM_MODEL';

export type PricePlan = {
  id: number;
  name: string;
  type?: PricePlanType;
  unit_size?: number;
  last_modified: string;
};

export type PricePlansResponse = BaseResponse<PricePlan[]> & {
  total_results: number;
};

/**
 * Optional qOverride lets callers pass a debounced search text directly.
 * When qOverride is provided (including empty string), URL search params are ignored
 * to prevent pollution from other pages' filter params (e.g., plan list filters).
 * Pass undefined to use URL search params for paginated list views.
 */
export const useGetPricePlansBo = (qOverride?: string) => {
  const { searchParams } = useNewAppSearchParams();

  // When qOverride is provided (even as empty string, e.g., from a dropdown search),
  // ignore URL params to avoid picking up filters from other pages.
  // Only use URL params when qOverride is explicitly undefined.
  const useUrlParams = qOverride === undefined;

  const offset =
    useUrlParams && Number.isFinite(Number(searchParams.offset))
      ? Number(searchParams.offset)
      : 0;
  const limit =
    useUrlParams && Number.isFinite(Number(searchParams.limit))
      ? Number(searchParams.limit)
      : 25;

  const sortField = useUrlParams
    ? (searchParams.sort as string | undefined) || undefined
    : undefined;
  const sortDirection = useUrlParams
    ? (searchParams.direction as 'asc' | 'desc' | undefined) || undefined
    : undefined;

  const qString = useUrlParams
    ? (searchParams.q as string | undefined)?.trim() || undefined
    : qOverride?.trim() || undefined;

  const fetchPricePlans = async () => {
    const { pricePlansBo } = GET_URL_NEW({
      offset,
      limit,
      q: qString,
      sort: sortField,
      direction: sortDirection
    });

    const { data } = await Axios.get<PricePlansResponse>(pricePlansBo);
    return data;
  };

  return useQuery<PricePlansResponse, AxiosError<BaseError>>(
    ['pricePlansBo', offset, limit, qString, sortField, sortDirection],
    fetchPricePlans,
    { keepPreviousData: true, useErrorBoundary: false }
  );
};
